import { useState } from 'react'
import { useFieldArray, UseFieldArrayAppend, UseFieldArrayRemove, UseFieldArrayReturn, UseFieldArrayUpdate, useFormContext, useFormState } from 'react-hook-form'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Alert, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'

import InfoSection from '@/components/ui/sections/InfoSection'
import { useIsSA } from '@/hooks/useIsSA'
import { parentValueGenerator } from '@/utils/patient'

import { PatientProfileFormData } from '../model/usePatientProfileForm'
import { ParentFormData } from '../model/useParentForm'
import ParentDialog from './ParentDialog'

export function ParentsSection({ readOnly }: { readOnly: boolean }) {
  useLingui()
  const isSA = useIsSA()
  const rhForm = useFormContext<PatientProfileFormData>()
  const { fields, append, remove, update } = useFieldArray({ control: rhForm.control, name: 'parents' })

  // The schema enforces at least one emergency contact for SEHA (en-SA); the
  // array-level error is not attached to any input so it must be surfaced here.
  // useFormState is the safe way to read form errors: reading
  // `rhForm.formState.errors.parents` directly in the render body loops forever
  // for useFieldArray fields ("Maximum update depth exceeded").
  const { errors } = useFormState({ control: rhForm.control, name: 'parents' })
  const parentsError = errors.parents
  const parentsErrorMessage = parentsError?.root?.message ?? (typeof parentsError?.message === 'string' ? parentsError.message : undefined)

  return (
    <InfoSection action={!readOnly && <AddParentButton append={append} isSA={isSA} />} label={t`Emergency Contacts`}>
      {!readOnly && parentsErrorMessage && (
        <Alert severity="error" variant="outlined" sx={{ mt: '10px' }}>
          {parentsErrorMessage}
        </Alert>
      )}
      <ParentsList fields={fields} remove={remove} readOnly={readOnly} update={update} />
    </InfoSection>
  )
}

function ParentsList({
  fields,
  remove,
  readOnly,
  update,
}: {
  fields: UseFieldArrayReturn<PatientProfileFormData, 'parents'>['fields']
  remove: UseFieldArrayRemove
  update: UseFieldArrayUpdate<PatientProfileFormData, 'parents'>
  readOnly: boolean
}) {
  if (!fields.length && readOnly) {
    return (
      <Alert icon={false} severity="info" variant="outlined" sx={{ mt: '10px' }}>
        <Trans>There are no emergency contacts for this patient</Trans>
      </Alert>
    )
  }

  return (
    <TableContainer component={Paper} sx={{ mt: '16px' }}>
      <Table>
        <TableBody>
          {fields.map((field, index) => (
            <TableRow key={field.id}>
              <TableCell component="th" scope="row">
                {`${field.firstname} ${field.lastname}`}
              </TableCell>
              <TableCell align="right">
                <Stack display="inline-flex" direction="row" spacing={1}>
                  <EditParentButton data={field} index={index} update={update} readOnly={readOnly} />
                  {!readOnly && (
                    <Tooltip title={t`Delete`}>
                      <IconButton onClick={() => remove(index)} size="small">
                        <DeleteRoundedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function AddParentButton({ append, isSA }: { append: UseFieldArrayAppend<PatientProfileFormData, 'parents'>; isSA: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Tooltip title={isSA ? t`Add an emergency contact` : t`Add a Parent`}>
        <IconButton onClick={() => setOpen(true)} size="small">
          <AddRoundedIcon />
        </IconButton>
      </Tooltip>
      <ParentDialog
        data={parentValueGenerator()}
        onClose={() => setOpen(false)}
        onSubmit={(data) => {
          append(data)
          setOpen(false)
        }}
        open={open}
      />
    </>
  )
}

function EditParentButton({
  data,
  index,
  update,
  readOnly,
}: {
  data: ParentFormData
  index: number
  update: UseFieldArrayUpdate<PatientProfileFormData, 'parents'>
  readOnly: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Tooltip title={t`Show`}>
        <IconButton onClick={() => setOpen(true)} size="small">
          <VisibilityRoundedIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <ParentDialog
        data={data}
        onClose={() => setOpen(false)}
        onSubmit={(data) => {
          update(index, data)
          setOpen(false)
        }}
        open={open}
        readOnly={readOnly}
      />
    </>
  )
}
